import {
  datadogLogs,
  LogsEvent,
  LogsEventDomainContext,
  LogsInitConfiguration,
} from "@datadog/browser-logs";
import {
  datadogRum,
  RumErrorEventDomainContext,
  RumEvent,
  RumEventDomainContext,
  RumFetchResourceEventDomainContext,
  RumInitConfiguration,
} from "@datadog/browser-rum";
import { RumErrorEvent } from "@datadog/browser-rum-core/src/rumEvent.types";
import { ENVIRONMENTS, PUBLIC_ENVIRONMENT } from "@/config";
import { getGraphqlURI } from "@/lib/apollo.utils";
import { DATADOG_ENABLED } from "@/utils/observability/const";
import { isCriticalWorkflow } from "@/utils/observability/criticalWorkflows";
import { DatadogAdditionalData } from "@/utils/observability/extractFetchData";
import { overrideFetch } from "@/utils/observability/fetch";

interface ExtendedRumFetchResourceEventDomainContext
  extends RumFetchResourceEventDomainContext {
  error?: Error & { datadogAdditionalData?: DatadogAdditionalData };
}

const ERROR_SUBSTRINGS_TO_IGNORE = [
  // caused by native-app, it has to throw this error for its navigation to work
  'Uncaught "Route change was aborted (this error can be safely ignored). See https://github.com/zeit/next.js/issues/2476."',

  // network errors:
  "Failed to fetch",
  "Failed to load",
  "error loading",
  "ChunkLoadError: Loading chunk",
  "TypeError: Importing a module script failed.",
  "network error",
  "NetworkError when attempting to fetch resource.",
  "Load failed",

  // clerk:
  "Cannot read properties of undefined (reading 'signUp')",
  "Error: ClerkJS: Token refresh failed",

  // twilio:
  'Error: Uncaught, unspecified "error" event. (false)',
  "Unable to open database file on disk",
  "n: disconnected (status: 0, code: 0)", // should be resolved after https://www.notion.so/moxie-docs/Messages-refactor-100-FE-111e63b2b0a380ca9a8ed126981b7b1f is done

  // safari:
  "NotSupportedError: Not implemented",
  "TypeError: FetchEvent.respondWith received an error: no-response", // network problem when navigating to a new page
  "UnknownError: Attempt to see if key exists in objectstore without an in-progress transaction",

  // 3rd party:
  "Script error.", // caused by 3rd party scripts, more info: https://sentry.io/answers/script-error/
  "Error: Rejected", // Raised by workbox. Cause: google crawler env limitations
  "TypeError: undefined is not an object (evaluating 'this._data[this._data.length-1].push')", // caused by old version of signature_pad which is dependency of react-signature-canvas
  "Ignored attempt to cancel a touchmove event with cancelable=false, for example because scrolling is in progress and cannot be interrupted.", // emitted by markerjs2 package, issue: https://github.com/ailon/markerjs2/issues/197

  // other
  "Message Timeout",
  "_AutofillCallbackHandler",
  "Error: Abort fetching component for route", // caused by multiple router.push(), might happen when user gets frustrated and clicks button a couple of time, or navigates to a diffrent page
  "AbortError: signal is aborted without reason",
  "AbortError: The operation was aborted.",
  "Error: Invalid call to runtime.sendMessage(). Tab not found.", // caused by some browser extension, no impact for the user
  "Failed to load static props", // no impact for the user
];

const initDatadog = () => {
  // We may move these to environment variables in the future
  // Currently, these are hardcoded because they do not change from env to env
  const DATADOG_APP_ID = "4db23ee5-b274-41b2-aed3-01e8c3684db2";
  const DATADOG_CLIENT_TOKEN = "pub6c1e78af147fc4d25bdb2fe5c6d6942c";

  /** Detect if error originates from BirdEatsBug */
  const isBirdEatsBugError = (stack: string): boolean => {
    const stackFrames = stack.split("\n").slice(1);
    return (
      stackFrames.length > 0 &&
      stackFrames[stackFrames.length - 1].includes("sdk.birdeatsbug.com")
    );
  };

  function shouldIgnoreError(event: RumErrorEvent | LogsEvent): boolean {
    const message = event.error.message ?? (event.message as string) ?? "";
    const stack = event.error.stack ?? "";

    // Ignore errors without stack trace (when stack contains only the error message)
    if (stack.split("\n").length === 1) {
      datadogLogs.logger.warn("Error without stack trace", { message, stack }); // TODO(pawel.wamej): remove after April 24th
      return true;
    }

    if (
      ERROR_SUBSTRINGS_TO_IGNORE.some(
        (e) => message.includes(e) || stack.includes(e)
      )
    ) {
      return true;
    }

    if (isBirdEatsBugError(stack)) {
      return true;
    }
  }

  function handleReport(
    error: RumErrorEvent["error"],
    context: RumErrorEventDomainContext
  ): boolean {
    if (!(context.error instanceof SecurityPolicyViolationEvent)) {
      return true;
    }
    const report = context.error as SecurityPolicyViolationEvent;
    if (URL.canParse(report.blockedURI)) {
      error.fingerprint = new URL(report.blockedURI).host;
    } else {
      error.fingerprint = report.blockedURI;
    }
    return true;
  }

  const handleRumBeforeSend: RumInitConfiguration["beforeSend"] = (
    event: RumEvent,
    context: RumEventDomainContext
  ) => {
    if (event.type == "error" && event.error.source === "report") {
      handleReport(event.error, context as RumErrorEventDomainContext);
    } else if (event.type === "error") {
      if (shouldIgnoreError(event)) {
        return false;
      }
      if (isCriticalWorkflow()) {
        event.context.critical_workflow = true;
      }
    } else if (event.type === "resource" && event.resource.type === "fetch") {
      const { response, error } =
        context as ExtendedRumFetchResourceEventDomainContext;
      if (response && response.datadogAdditionalData) {
        event.context = {
          ...event.context,
          ...response.datadogAdditionalData,
        };
      } else if (error && error.datadogAdditionalData) {
        event.context = {
          ...event.context,
          ...error.datadogAdditionalData,
        };
      }
    }
    return true;
  };

  datadogRum.init({
    applicationId: DATADOG_APP_ID,
    clientToken: DATADOG_CLIENT_TOKEN,
    site: process.env.NEXT_PUBLIC_DATADOG_SITE,
    service: "moxie_suite_ui",
    env:
      PUBLIC_ENVIRONMENT === ENVIRONMENTS.PRODUCTION
        ? "prod"
        : PUBLIC_ENVIRONMENT,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    version: process.env.NEXT_PUBLIC_MOXIEINFRA_COMMIT_SHA,
    defaultPrivacyLevel: "mask-user-input",
    compressIntakeRequests: true,
    allowedTracingUrls: [
      {
        match: getGraphqlURI(),
        propagatorTypes: ["tracecontext"],
      },
      {
        match: process.env.NEXT_PUBLIC_DJANGO_URI,
        propagatorTypes: ["tracecontext"],
      },
    ],
    beforeSend: handleRumBeforeSend,
  });

  const handleLogsBeforeSend: LogsInitConfiguration["beforeSend"] = (
    event: LogsEvent,
    _: LogsEventDomainContext // eslint-disable-line @typescript-eslint/no-unused-vars
  ) => {
    if (event.error && shouldIgnoreError(event)) {
      return false;
    }
    return true;
  };

  datadogLogs.init({
    clientToken: DATADOG_CLIENT_TOKEN,
    site: process.env.NEXT_PUBLIC_DATADOG_SITE,
    service: "moxie_suite_ui",
    env:
      PUBLIC_ENVIRONMENT === ENVIRONMENTS.PRODUCTION
        ? "prod"
        : PUBLIC_ENVIRONMENT,
    version: process.env.NEXT_PUBLIC_MOXIEINFRA_COMMIT_SHA,
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    forwardConsoleLogs: "all",
    beforeSend: handleLogsBeforeSend,
  });

  // configure logger to send logs to datadog and log them in the console
  datadogLogs.logger.setHandler(["console", "http"]);
};

if (DATADOG_ENABLED) {
  overrideFetch();
  initDatadog();
} else {
  // forward errorLogger logs into console
  datadogLogs.logger.setHandler("console");
}

export default function DatadogInit() {
  return null;
}
